// Copyright (c) 2025 embyt GmbH. All rights reserved.
// Author: Roman Morawek
import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, WrappedComponentProps, injectIntl, FormattedMessage } from "react-intl";
import ResizeObserver from "resize-observer-polyfill";
import { DateTime } from "luxon";

import { Theme } from "types";
import { IAction } from "actions";
import { IState } from "states/states";
import { formatPowerValue, formatEnergyValue, id2Index, getTheme } from "guiutils";
import { isSet, getLocalTime, round } from "utils";
import {
  IInput,
  IOutput,
  ITariffCircuit,
  PowerUnit,
  CustomDashboard,
  CustomDashboardValues,
} from "parameter";
import { ITrendData } from "states/trendingstates";
import { ITariffCircuitData } from "states/datastates";

import CustomDashboardEnergyGraph from "view/comp/CustomDashboardEnergyGraph";

import { ReactComponent as SvgArrowLeft } from "static/customdashboard/arrow-left.svg";
import { ReactComponent as SvgArrowRight } from "static/customdashboard/arrow-right.svg";
import { ReactComponent as SvgBattery } from "static/customdashboard/battery.svg";
import { ReactComponent as SvgChargingPile } from "static/customdashboard/charging-pile.svg";
import { ReactComponent as SvgHouse } from "static/customdashboard/house.svg";
import { ReactComponent as SvgPhotovoltaik } from "static/customdashboard/photovoltaik.svg";
import { ReactComponent as SvgBolt } from "static/customdashboard/bolt.svg";
import { ReactComponent as SvgPlug } from "static/customdashboard/plug.svg";
import { ReactComponent as SvgPowerGrid } from "static/customdashboard/power_grid.svg";
import { ReactComponent as SvgOutside } from "static/customdashboard/outside.svg";
import { ReactComponent as SvgInside } from "static/customdashboard/inside.svg";
import { ReactComponent as SvgSavings } from "static/customdashboard/savings.svg";
import { ReactComponent as SvgWaveSine } from "static/customdashboard/wave-sine.svg";
import { ReactComponent as SvgEpmLogo } from "static/logoText.svg";

import "./CustomDashboard.scss";

// uses webpack definitions
declare const IS_CLOUD: boolean;

const ANIMATION_CHANGE_TOLERANCE = 0.3; // tolerance for animation duration changes

interface ICustomDashboardPropsExt {
  isTopLevel: boolean; // true if this is shown fullscreen
}

interface ICustomDashboardProps {
  settings: CustomDashboard | undefined;
  gridImportPower: number | undefined;
  gridExportPower: number | undefined;
  gridExportEnergy: number | undefined;
  gridImportEnergy: number | undefined;
  batteryPower: number | undefined;
  batteryEnergy: number | undefined;
  pvPower: number | undefined;
  pvEnergy: number | undefined;
  homePower: number | undefined;
  homeEnergy: number | undefined;
  custom1Power: number | undefined;
  custom1Energy: number | undefined;
  custom2Power: number | undefined;
  custom2Energy: number | undefined;
  custom3Power: number | undefined;
  custom3Energy: number | undefined;
  dashboardValues: CustomDashboardValues;
  powerUnit: PowerUnit | undefined;
  savingsParameter: IInput | IOutput | undefined;
  socBatteryParameter: IInput | IOutput | undefined;
  socCustom1Parameter: IInput | IOutput | undefined;
  tariffline: ITariffCircuit[] | undefined;
}

interface ICustomDashboardState {
  currentTime: string;
}

// helper function to calculate power from tariff circuits and return the unit
function calculatePowerFromTariffCircuits(
  tariffCircuitIds: number[] | undefined,
  tariffCircuitPara: ITariffCircuit[] | undefined,
  tariffCircuitData: ITariffCircuitData[],
): { power: number | undefined; unit: PowerUnit | undefined } {
  if (!tariffCircuitIds || tariffCircuitIds.length === 0 || !isSet(tariffCircuitPara)) {
    return { power: undefined, unit: undefined };
  }

  let totalPower = 0;
  let unit: PowerUnit | undefined;
  // get the first valid tariff circuit to determine the unit
  for (const id of tariffCircuitIds) {
    const doInvert = id < 0;
    const index = id2Index(tariffCircuitPara, Math.abs(id));
    if (isSet(index) && isSet(tariffCircuitData[index]?.power)) {
      const power = (doInvert ? -1 : 1) * tariffCircuitData[index].power;
      totalPower += isNaN(power) ? 0 : power;
    }
    // get the unit from the first tariff circuit
    if (!isSet(unit) && isSet(index) && tariffCircuitPara[index]?.unit) {
      unit = tariffCircuitPara[index].unit;
    }
  }
  return { power: totalPower, unit };
}

// helper function to calculate energy from trend data for the last days
function calculateEnergyFromTrendData(
  tariffCircuitIds: number[] | undefined,
  tariffCircuitPara: ITariffCircuit[] | undefined,
  trendData: ITrendData,
  startTimestamp: number,
  onlyPositive: boolean,
): number | undefined {
  if (!tariffCircuitIds || tariffCircuitIds.length === 0) {
    return undefined;
  }
  const timestamps = Object.keys(trendData)
    .map(Number)
    .filter((curItem) => curItem >= startTimestamp);
  // sum trending data
  let totalEnergy: number | undefined;
  tariffCircuitIds.forEach((id) => {
    const doInvert = id < 0;
    const tcIndex = id2Index(tariffCircuitPara, Math.abs(id));
    timestamps.forEach((curTimestamp) => {
      const trendDataTimestamp = trendData[curTimestamp];
      // for energy, we only count positive values
      if (isSet(tcIndex) && isSet(trendDataTimestamp[tcIndex]?.energy)) {
        if (!isSet(totalEnergy)) {
          totalEnergy = 0;
        }
        const energy = (doInvert ? -1 : 1) * trendDataTimestamp[tcIndex].energy;
        totalEnergy += onlyPositive ? Math.max(0, energy) : energy;
      }
    });
  });
  return totalEnergy;
}

const mapStateToProps = (state: IState) => {
  const site = state.site.siteList?.find((curSite) => curSite.id === state.cached.selectedSite);
  if (IS_CLOUD && site === undefined) {
    console.error("admin unidentified site", state.cached.selectedSite, state.site.siteList);
  }
  // settings may be stored in cloud or in local parameter set
  // cloud settings have priority over local settings
  const settings =
    site?.cloudSettings?.customDashboard ??
    state.parameter?.general.customSettings?.customDashboard;

  // get power values and units from tariffcircuit data
  const calcPower = (tlIds: number[] | undefined) => {
    return calculatePowerFromTariffCircuits(
      tlIds,
      state.parameter?.tariffline,
      state.data.tariffcircuit,
    );
  };
  const gridImportResult = calcPower(settings?.tariffCircuits?.gridImport);
  const gridExportResult = calcPower(settings?.tariffCircuits?.gridExport);
  const batteryResult = calcPower(settings?.tariffCircuits?.battery);
  const pvResult = calcPower(settings?.tariffCircuits?.pv);
  const homeResult = calcPower(settings?.tariffCircuits?.home);
  const custom1Result = calcPower(settings?.tariffCircuits?.custom1);
  const custom2Result = calcPower(settings?.tariffCircuits?.custom2);
  const custom3Result = calcPower(settings?.tariffCircuits?.custom3);

  // extract units (use the first available relevant unit)
  const powerUnit = gridImportResult.unit ?? gridExportResult.unit ?? PowerUnit.KWH;

  // get energy values from trending data for the last days
  const timerange = (IS_CLOUD ? settings?.timerange ?? 7 : 1) - 1;
  const startTimestamp = DateTime.local().minus({ days: timerange }).startOf("day").toSeconds();
  const calcEnergy = (tlIds: number[] | undefined, onlyPositive = false) => {
    return calculateEnergyFromTrendData(
      tlIds,
      state.parameter?.tariffline,
      state.trending.trendData,
      startTimestamp,
      onlyPositive,
    );
  };
  const gridExportEnergy = calcEnergy(settings?.tariffCircuits?.gridExport, true);
  const gridImportEnergy = calcEnergy(settings?.tariffCircuits?.gridImport, true);
  const batteryEnergy = calcEnergy(settings?.tariffCircuits?.battery);
  const pvEnergy = calcEnergy(settings?.tariffCircuits?.pv);
  const homeEnergy = calcEnergy(settings?.tariffCircuits?.home);
  const custom1Energy = calcEnergy(settings?.tariffCircuits?.custom1);
  const custom2Energy = calcEnergy(settings?.tariffCircuits?.custom2);
  const custom3Energy = calcEnergy(settings?.tariffCircuits?.custom3);

  // get various input data
  const getIoData = (item: keyof CustomDashboardValues) => {
    const inIndex = id2Index(state.parameter?.input, settings?.inputs?.[item]);
    const outIndex = id2Index(state.parameter?.output, settings?.outputs?.[item]);
    const value = isSet(inIndex)
      ? state.data.input[inIndex]
      : isSet(outIndex)
      ? state.data.output[outIndex].logical
      : undefined;
    return value;
  };
  const dashboardValues: CustomDashboardValues = {
    tempOut: getIoData("tempOut"),
    tempIn: getIoData("tempIn"),
    savings: getIoData("savings"),
    socBattery: getIoData("socBattery"),
    socCustom1: getIoData("socCustom1"),
  };
  const getIoParameter = (item: keyof CustomDashboardValues) =>
    state.parameter?.input.find(
      (curItem, curIndex) => (curItem.id ?? curIndex) === settings?.inputs?.[item],
    ) ??
    state.parameter?.output.find(
      (curItem, curIndex) => (curItem.id ?? curIndex) === settings?.outputs?.[item],
    );
  const socCustom1Parameter = getIoParameter("socCustom1");
  const socBatteryParameter = getIoParameter("socBattery");
  const savingsParameter = getIoParameter("savings");

  const props: ICustomDashboardProps = {
    settings,
    gridImportPower: gridImportResult.power,
    gridExportPower: gridExportResult.power,
    batteryPower: batteryResult.power,
    pvPower: pvResult.power,
    homePower: homeResult.power,
    custom1Power: custom1Result.power,
    custom2Power: custom2Result.power,
    custom3Power: custom3Result.power,
    gridExportEnergy,
    gridImportEnergy,
    batteryEnergy,
    pvEnergy,
    homeEnergy,
    custom1Energy,
    custom2Energy,
    custom3Energy,
    dashboardValues,
    powerUnit,
    socCustom1Parameter,
    socBatteryParameter,
    savingsParameter,
    tariffline: state.parameter?.tariffline,
  };
  return props;
};

interface ICustomDashboardDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ICustomDashboardDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({});

class CustomDashboardComponent extends React.Component<
  ICustomDashboardPropsExt &
    ICustomDashboardProps &
    ICustomDashboardDispatch &
    WrappedComponentProps,
  ICustomDashboardState
> {
  private resizeObserver: ResizeObserver | undefined;
  private timer: NodeJS.Timeout | undefined;
  private dashboardRef: React.RefObject<HTMLDivElement>;
  private resizeTimeout: NodeJS.Timeout | undefined;

  constructor(
    props: ICustomDashboardPropsExt &
      ICustomDashboardProps &
      ICustomDashboardDispatch &
      WrappedComponentProps,
  ) {
    super(props);
    this.state = {
      currentTime: getLocalTime(Date.now()),
    };
    this.dashboardRef = React.createRef();
  }

  componentDidMount() {
    this.resizeObserver = new ResizeObserver(() => {
      // Debounce to avoid ResizeObserver loop errors
      if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.drawAllConnections();
      }, 50); // 50ms debounce
    });
    if (this.dashboardRef.current) this.resizeObserver.observe(this.dashboardRef.current);
    else console.warn("could not register resize observer");

    // update time every minute
    this.timer = setInterval(() => {
      this.setState({
        currentTime: getLocalTime(Date.now()),
      });
    }, 60000); // 60000 ms = 1 minute

    // set initial animation durations
    this.updateAnimationDurations();
  }

  componentDidUpdate(prevProps: ICustomDashboardProps) {
    // Update animation durations when power values change
    if (
      prevProps.gridImportPower !== this.props.gridImportPower ||
      prevProps.gridExportPower !== this.props.gridExportPower ||
      prevProps.batteryPower !== this.props.batteryPower ||
      prevProps.pvPower !== this.props.pvPower ||
      prevProps.homePower !== this.props.homePower ||
      prevProps.custom1Power !== this.props.custom1Power ||
      prevProps.custom2Power !== this.props.custom2Power ||
      prevProps.custom3Power !== this.props.custom3Power
    ) {
      this.updateAnimationDurations();
    }
  }

  componentWillUnmount() {
    // Clear the timer when component unmounts
    if (this.timer) {
      clearInterval(this.timer);
    }
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
  }

  /**
   * Redraws all visible connectors based on current tile layout.
   * Automatically hides paths from hidden tiles.
   */
  private drawAllConnections() {
    const svg = document.querySelector(".connectors") as SVGSVGElement;
    const tiles = document.querySelector(".dashboard__tiles") as HTMLDivElement;
    if (!svg || !tiles) return;

    const tilesRect = tiles.getBoundingClientRect();
    svg.style.width = `${tilesRect.width}px`;
    svg.style.height = `${tilesRect.height}px`;
    svg.setAttribute("width", String(tilesRect.width));
    svg.setAttribute("height", String(tilesRect.height));

    const connections = [
      { position: "top-left" },
      { position: "top-center" },
      { position: "top-right" },
      { position: "bottom-left" },
      { position: "bottom-center" },
      { position: "bottom-right" },
    ];

    connections.forEach(({ position }) => {
      this.connectTiles(position);
    });
  }

  /**
   * Connects two tiles with an animated SVG path.
   * Used for visualizing energy flow into the central CO₂ tile.
   */
  private connectTiles(position: string) {
    const fromEl = document.getElementById(`tile-${position}`)?.querySelector(".tile");
    const toEl = document.getElementById("tile-middle-center");
    const path = document.getElementById(`line-${position}`);
    const svg = document.querySelector(".connectors");
    if (!fromEl || !toEl || !path || !svg) {
      // this may happen if a tile is not existing in this site
      //console.log("no connect", position, fromEl, toEl, path);
      return;
    }

    const svgRect = svg.getBoundingClientRect();
    const fromRect = fromEl.getBoundingClientRect();
    const toRect = toEl.getBoundingClientRect();
    const from = {
      x: (fromRect.left + fromRect.right) / 2 - svgRect.left,
      y: (fromRect.top + fromRect.bottom) / 2 - svgRect.top,
    };
    const verticalOffset = ((position.startsWith("top") ? -1 : 1) * toRect.height) / 6;
    const to = {
      x: (toRect.left + toRect.right) / 2 - svgRect.left,
      y: (toRect.top + toRect.bottom) / 2 - svgRect.top + verticalOffset,
    };
    const radius = 8;
    const isBelow = from.y > to.y;
    const verticalStop = isBelow ? to.y + radius : to.y - radius;
    const horizontalStart = from.x;
    const horizontalStop = from.x < to.x ? from.x + radius : from.x - radius;

    const d = `
      M ${from.x},${from.y}
      L ${horizontalStart},${verticalStop}
      Q ${horizontalStart} ${to.y} ${horizontalStop} ${to.y}
      L ${to.x},${to.y}
    `;
    path.setAttribute("d", d.trim());
  }

  // calculate animation duration based on power value
  // higher power = faster animation (lower duration)
  private calculateAnimationDuration(power: number | undefined) {
    const nominalPower = this.props.settings?.nominalPower ?? 100;
    if (!isSet(power) || power === 0 || nominalPower === 0) {
      return undefined;
    }

    // base calculation: inverse relationship between power and duration
    // higher power = lower duration (faster animation)
    // we use a minimum duration to prevent animations from being too fast
    const minDuration = 0.5; // s, fastest animation duration in seconds
    const maxDuration = 10; // s, slowest animation duration in seconds
    const defaultDuration = 1.5; // s, duration at nominal power

    // use absolute value since we care about magnitude, not direction
    const normalizedPower = Math.abs(power) / nominalPower;
    // avoid division by zero if power is zero
    const duration = normalizedPower ? defaultDuration / normalizedPower : maxDuration;
    return Math.max(minDuration, Math.min(maxDuration, duration));
  }

  // helper function to set animation properties based on power value
  private setAnimationDuration(position: string, power: number | undefined) {
    const element = document.getElementById(`line-${position}`);
    const newValue = this.calculateAnimationDuration(power);
    if (!element || !isSet(newValue) || isNaN(newValue)) {
      // this may happen if a tile is not existing in this site
      return;
    }
    const oldValue = parseFloat(element.style.getPropertyValue(`--${position}-animation-duration`));
    // set animation duration, but only if it has changed significantly
    // this shall inhibit flickering when animation duration changes
    if (isNaN(oldValue) || Math.abs(newValue - oldValue) / newValue > ANIMATION_CHANGE_TOLERANCE) {
      element.style.setProperty(`--${position}-animation-duration`, `${newValue}s`);
    }
  }

  // update CSS variables for animation durations, names, and colors
  private updateAnimationDurations() {
    const gridPower = this.props.gridImportPower
      ? this.props.gridImportPower
      : this.props.gridExportPower
      ? -this.props.gridExportPower
      : undefined;
    // set animation properties for each power source
    this.setAnimationDuration("top-left", gridPower);
    this.setAnimationDuration("top-center", this.props.batteryPower);
    this.setAnimationDuration("top-right", this.props.pvPower);
    this.setAnimationDuration("bottom-left", this.props.custom1Power);
    this.setAnimationDuration("bottom-center", this.props.custom2Power);
    this.setAnimationDuration("bottom-right", this.props.custom3Power);
  }

  public render() {
    const { settings, powerUnit } = this.props;
    if (!isSet(settings)) {
      return (
        <div
          id="customdashboard"
          className={this.props.isTopLevel ? "toplevel" : undefined}
          ref={this.dashboardRef}
        >
          <FormattedMessage id="Custom Dashboard is not configured for this site." />
        </div>
      );
    }
    const getTlParameter = (item: keyof CustomDashboard["tariffCircuits"]) =>
      this.props.tariffline?.find(
        (curItem, curIndex) =>
          (curItem.id ?? curIndex) === this.props.settings?.tariffCircuits?.[item]?.[0],
      );
    const custom1Parameter = getTlParameter("custom1");
    const custom2Parameter = getTlParameter("custom2");
    const custom3Parameter = getTlParameter("custom3");

    const gridPower = this.props.gridImportPower
      ? this.props.gridImportPower
      : this.props.gridExportPower
      ? -this.props.gridExportPower
      : undefined;
    const getFlowClass = (power: number | undefined, isInverted?: boolean) => {
      const result = !power
        ? "flow-none"
        : (power > 0 && !isInverted) || (power < 0 && isInverted)
        ? "flow-out"
        : "flow-in";
      //console.log("getFlowClass", power, isInverted, result);
      return result;
    };
    const formatPower = (power: number | undefined) => {
      return formatPowerValue(Math.abs(power ?? 0), powerUnit);
    };

    return (
      <div
        id="customdashboard"
        className={this.props.isTopLevel ? "toplevel" : undefined}
        ref={this.dashboardRef}
      >
        <section className="dashboard__main">
          {(isSet(settings?.title) || isSet(settings?.preTitle)) && (
            <header className="dashboard__header">
              <div className="dashboard__branding">
                {isSet(settings.preTitle) && (
                  <h2 className="dashboard__subtitle">{settings.preTitle}</h2>
                )}
                {isSet(settings.title) && <h1 className="dashboard__heading">{settings.title}</h1>}
              </div>
              <div className="dashboard__topright">
                {getTheme() === Theme.EPM && (
                  <div className="dashboard__logo">
                    <SvgEpmLogo />
                  </div>
                )}
                <div className="dashboard__status" data-status-type="updating">
                  <SvgWaveSine className="dashboard__status-icon" />
                  <span className="dashboard__status-text">
                    <FormattedMessage id="Realtime Update" />
                  </span>
                </div>
              </div>
            </header>
          )}

          <div className="dashboard__tiles-wrapper">
            <div className="dashboard__tiles">
              <div id="top-row" className="tiles-row">
                <div id="tile-top-left" className="tile-item">
                  {isSet(gridPower) && (
                    <div className="tile">
                      <div className="tile__content">
                        <div className="tile__value">{formatPower(gridPower)}</div>
                        <div className="tile__label">
                          {gridPower && gridPower < 0 ? (
                            <FormattedMessage id="Grid Export" />
                          ) : (
                            <FormattedMessage id="Grid Import" />
                          )}
                        </div>
                      </div>
                      <div className="tile__icon">
                        <SvgPowerGrid />
                      </div>
                    </div>
                  )}
                </div>

                <div id="tile-top-center" className="tile-item">
                  {isSet(this.props.batteryPower) && (
                    <div className="tile">
                      <div className="tile__content">
                        <div className="tile__value">{formatPower(this.props.batteryPower)}</div>
                        <div className="tile__label">
                          {this.props.batteryPower === 0 ? (
                            <FormattedMessage id="Battery Storage" />
                          ) : this.props.batteryPower > 0 ? (
                            <FormattedMessage id="Battery Charging" />
                          ) : (
                            <FormattedMessage id="Battery Discharging" />
                          )}
                        </div>
                      </div>
                      <div className="tile__icon">
                        {isSet(this.props.dashboardValues.socBattery) && (
                          <div className="tile__soc">
                            {this.props.dashboardValues.socBattery.toFixed(0)}{" "}
                            {this.props.socBatteryParameter?.unit}
                          </div>
                        )}
                        <SvgBattery />
                      </div>
                    </div>
                  )}
                </div>

                <div id="tile-top-right" className="tile-item">
                  {isSet(this.props.pvPower) && (
                    <div className="tile">
                      <div className="tile__content">
                        <div className="tile__value">{formatPower(this.props.pvPower)}</div>
                        <div className="tile__label">
                          <FormattedMessage id="Photovoltaic" />
                        </div>
                      </div>
                      <div className="tile__icon">
                        <SvgPhotovoltaik />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div id="tile-middle-center">
                <div className="tile-center__icon">
                  <SvgHouse />
                </div>
                <div className="tile-center__content">
                  {isSet(this.props.settings?.tariffCircuits.home) && (
                    <div className="tile__value">{formatPower(this.props.homePower)}</div>
                  )}
                  {settings?.sitetext1 && (
                    <div className={"tile-center__label1"}>{settings.sitetext1}</div>
                  )}
                  {settings?.sitetext2 && (
                    <div className="tile-center__label2">{settings.sitetext2}</div>
                  )}
                </div>
              </div>

              <div id="bottom-row" className="tiles-row">
                <div id="tile-bottom-left" className="tile-item">
                  {isSet(this.props.custom1Power) && (
                    <div className="tile">
                      <div className="tile__content">
                        <div className="tile__value">{formatPower(this.props.custom1Power)}</div>
                        <div className="tile__label">
                          <FormattedMessage id="E-Mobility" />
                        </div>
                      </div>
                      <div className="tile__icon">
                        {isSet(this.props.dashboardValues.socCustom1) && (
                          <div className="tile__soc">
                            {this.props.dashboardValues.socCustom1.toFixed(0)}{" "}
                            {this.props.socCustom1Parameter?.unit}
                          </div>
                        )}
                        <SvgChargingPile />
                      </div>
                    </div>
                  )}
                </div>
                <div id="tile-bottom-center" className="tile-item">
                  {isSet(this.props.custom2Power) && (
                    <div className="tile">
                      <div className="tile__content">
                        <div className="tile__value">{formatPower(this.props.custom2Power)}</div>
                        <div className="tile__label">{custom2Parameter?.name}</div>
                      </div>
                      <div className="tile__icon">
                        <SvgBolt />
                      </div>
                    </div>
                  )}
                </div>

                <div id="tile-bottom-right" className="tile-item">
                  {isSet(this.props.custom3Power) && (
                    <div className="tile">
                      <div className="tile__content">
                        <div className="tile__value">{formatPower(this.props.custom3Power)}</div>
                        <div className="tile__label">
                          {this.props.settings?.custom3Name ?? <FormattedMessage id="Other" />}
                        </div>
                      </div>
                      <div className="tile__icon">
                        <SvgPlug />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <svg className="connectors">
              {isSet(gridPower) && (
                <path
                  id="line-top-left"
                  className={`connector-line ${getFlowClass(gridPower, true)}`}
                />
              )}
              {isSet(this.props.batteryPower) && (
                <path
                  id="line-top-center"
                  className={`connector-line ${getFlowClass(this.props.batteryPower)}`}
                />
              )}
              {isSet(this.props.pvPower) && (
                <path
                  id="line-top-right"
                  className={`connector-line ${getFlowClass(this.props.pvPower, true)}`}
                />
              )}
              {isSet(this.props.custom1Power) && (
                <path
                  id="line-bottom-left"
                  className={`connector-line ${getFlowClass(this.props.custom1Power)}`}
                />
              )}
              {isSet(this.props.custom2Power) && (
                <path
                  id="line-bottom-center"
                  className={`connector-line ${getFlowClass(this.props.custom2Power)}`}
                />
              )}
              {isSet(this.props.custom3Power) && (
                <path
                  id="line-bottom-right"
                  className={`connector-line ${getFlowClass(this.props.custom3Power)}`}
                />
              )}
            </svg>
          </div>
        </section>
        {this.renderSidebar()}
      </div>
    );
  }

  private renderSidebar() {
    const getTlParameter = (item: keyof CustomDashboard["tariffCircuits"]) =>
      this.props.tariffline?.find(
        (curItem, curIndex) =>
          (curItem.id ?? curIndex) === this.props.settings?.tariffCircuits?.[item]?.[0],
      );
    const custom1Parameter = getTlParameter("custom1");
    const custom2Parameter = getTlParameter("custom2");
    const custom3Parameter = getTlParameter("custom3");
    const formatEnergy = (energy: number | undefined, unit: PowerUnit | undefined) =>
      isSet(energy) ? formatEnergyValue(Math.abs(energy), unit ?? this.props.powerUnit) : "-";

    return (
      <aside className="dashboard__sidebar">
        <div className="sidebar">
          <div className="sidebar__content">
            <div className="sidebar__header">
              <div className="sidebar__time">{this.state.currentTime.substring(0, 5)}</div>
              {(isSet(this.props.dashboardValues.tempOut) ||
                isSet(this.props.dashboardValues.tempIn)) && (
                <>
                  <svg className="sidebar__divider" viewBox="0 0 10 100" width="10" height="100">
                    <path d="M5,10 V90" stroke="white" strokeWidth="2" />
                  </svg>
                  <div className="sidebar__temp">
                    {isSet(this.props.dashboardValues.tempOut) && (
                      <div className="sidebar__temp-item">
                        {isSet(this.props.dashboardValues.tempIn) && (
                          <SvgOutside className="sidebar__temp-icon" />
                        )}
                        <div className="sidebar__temp-value">
                          {`${this.props.dashboardValues.tempOut.toFixed(1)} °C`}
                        </div>
                      </div>
                    )}
                    {isSet(this.props.dashboardValues.tempIn) && (
                      <div className="sidebar__temp-item">
                        {isSet(this.props.dashboardValues.tempOut) && (
                          <SvgInside className="sidebar__temp-icon" />
                        )}
                        <div className="sidebar__temp-value">
                          {`${this.props.dashboardValues.tempIn.toFixed(1)} °C`}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {isSet(this.props.dashboardValues.savings) && (
              <div className="sidebar__savings">
                <SvgSavings className="savings__icon" />
                <div className="savings__content">
                  <div className="savings__value">
                    {round(this.props.dashboardValues.savings)} {this.props.savingsParameter?.unit}
                  </div>
                  <div className="savings__label">{this.props.savingsParameter?.name}</div>
                </div>
              </div>
            )}

            <div className="sidebar__data">
              <div className="sidebar__data-title">
                {IS_CLOUD ? (
                  <>
                    <FormattedMessage id="Last" /> {this.props.settings?.timerange ?? 7}{" "}
                    <FormattedMessage id="Days" />
                  </>
                ) : (
                  <FormattedMessage id="Today" />
                )}
              </div>
              <div className="sidebar__metrics">
                <div className="sidebar__metrics-grid">
                  {isSet(this.props.settings?.tariffCircuits.gridExport) && (
                    <div id="energy-export" className="metric">
                      <SvgArrowLeft
                        className={`metric__icon ${
                          this.props.settings?.graph?.separateGridImport ? "export" : ""
                        }`}
                      />
                      <div className="metric__content">
                        <div className="metric__label">
                          <FormattedMessage id="Export" />
                        </div>
                        <div className="metric__value">
                          {formatEnergy(this.props.gridExportEnergy, this.props.powerUnit)}
                        </div>
                      </div>
                    </div>
                  )}
                  {isSet(this.props.settings?.tariffCircuits.gridImport) && (
                    <div id="energy-import" className="metric">
                      <SvgArrowRight className="metric__icon" />
                      <div className="metric__content">
                        <div className="metric__label">
                          <FormattedMessage id="Import" />
                        </div>
                        <div className="metric__value">
                          {formatEnergy(this.props.gridImportEnergy, this.props.powerUnit)}
                        </div>
                      </div>
                    </div>
                  )}
                  {isSet(this.props.settings?.tariffCircuits.battery) && (
                    <div id="energy-battery" className="metric">
                      <SvgBattery className="metric__icon" />
                      <div className="metric__content">
                        <div className="metric__label">
                          <FormattedMessage id="Battery" />
                        </div>
                        <div className="metric__value">
                          {formatEnergy(this.props.batteryEnergy, this.props.powerUnit)}
                        </div>
                      </div>
                    </div>
                  )}
                  {isSet(this.props.settings?.tariffCircuits.pv) && (
                    <div id="energy-pv" className="metric">
                      <SvgPhotovoltaik className="metric__icon" />
                      <div className="metric__content">
                        <div className="metric__label">
                          <FormattedMessage id="Photovoltaic" />
                        </div>
                        <div className="metric__value">
                          {formatEnergy(this.props.pvEnergy, this.props.powerUnit)}
                        </div>
                      </div>
                    </div>
                  )}
                  {isSet(this.props.settings?.tariffCircuits.custom1) && (
                    <div id="energy-emobility" className="metric">
                      <SvgChargingPile className="metric__icon" />
                      <div className="metric__content">
                        <div className="metric__label">
                          <FormattedMessage id="E-Mobility" />
                        </div>
                        <div className="metric__value">
                          {formatEnergy(
                            this.props.custom1Energy,
                            custom1Parameter?.unit ?? this.props.powerUnit,
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {isSet(this.props.settings?.tariffCircuits.custom2) && (
                    <div id="energy-other" className="metric">
                      <SvgBolt className="metric__icon" />
                      <div className="metric__content">
                        <div className="metric__label">{custom2Parameter?.name}</div>
                        <div className="metric__value">
                          {formatEnergy(
                            this.props.custom2Energy,
                            custom2Parameter?.unit ?? this.props.powerUnit,
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {isSet(this.props.settings?.tariffCircuits.custom3) && (
                    <div id="energy-usage" className="metric">
                      <SvgPlug className="metric__icon" />
                      <div className="metric__content">
                        <div className="metric__label">
                          {this.props.settings?.custom3Name ?? <FormattedMessage id="Other" />}
                        </div>
                        <div className="metric__value">
                          {formatEnergy(
                            this.props.custom3Energy,
                            custom3Parameter?.unit ?? this.props.powerUnit,
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <CustomDashboardEnergyGraph />
          </div>
        </div>
      </aside>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(CustomDashboardComponent));
